import { CellAction, useCellActionsExecutor } from 'context/CellActions';
import useFilteredAbbreviations from 'modules/Abbreviations/hooks/useFilteredAbbreviations';
import React, { useState } from 'react';
import { TextAbbreviationDocument } from 'models';
import { intlGet } from 'utils/intlGet';
import useAbbreviation from '../../../hooks/useAbbreviation';
import AbbreviationDetails from '../../AbbreviationDetails';
import css from '../styles.module.scss';
import AbbreviationsList from './AbbreviationsList';
import SelectedText from './SelectedText';

type Props = {
  selectedText: string;
  abbreviationDocumentId?: TextAbbreviationDocument['id'];
  onChange: (abbreviationDocumentId?: TextAbbreviationDocument['id']) => void;
  dataTestId?: string;
};

const TextAbbreviationPanel: React.FunctionComponent<Props> = (props) => {
  const { selectedText, abbreviationDocumentId, onChange, dataTestId = 'textAbbreviationPanel' } = props;

  const abbreviationDocument = useAbbreviation(abbreviationDocumentId);
  const [showList, setShowList] = useState<boolean>(false);
  const { abbreviationsList, caseCorrectedTerm } = useFilteredAbbreviations(selectedText);

  useCellActionsExecutor(CellAction.CHANGE_ABBREVIATION, (id) => {
    if (abbreviationDocumentId !== id) {
      return false;
    }
    setShowList(true);

    return true;
  });

  if (abbreviationDocument && !showList) {
    return (
      <AbbreviationDetails
        abbreviation={abbreviationDocument.abbreviation}
        onChange={(): void => setShowList(true)}
        onRemove={(): void => onChange(undefined)}
        dataTestId={`${dataTestId}-details`}
      />
    );
  }

  return (
    <div className={css.flexContainer} data-testid={`${dataTestId}-container`}>
      <div className="d-flex justify-content-between align-items-start mb-2">
        <SelectedText text={caseCorrectedTerm} dataTestId={`${dataTestId}-selectedText`} />
        {abbreviationDocument && (
          <button
            className={css.button}
            onClick={(): void => setShowList(false) }
            data-testid={`${dataTestId}-button`}
          >
            {intlGet('EditorToolbar.Abbreviation', 'Button.Cancel')}
          </button>
        )}
      </div>
      <AbbreviationsList
        className={css.scroll}
        term={caseCorrectedTerm}
        abbreviationsList={abbreviationsList}
        abbreviationDocument={abbreviationDocument}
        onSelect={(_abbreviationDocumentId): void => {
          onChange(_abbreviationDocumentId);
          setShowList(false);
        }}
        dataTestId={`${dataTestId}-list`}
      />
    </div>
  );
};

export default TextAbbreviationPanel;
